import React from 'react';
import { Modal, Button, Form, Alert, Spinner, Accordion, Badge, Table, OverlayTrigger, Tooltip, Card } from 'react-bootstrap';
import OrderApi from '../../api/OrderApi';

type BuyerWarehouseResolution =
    | 'MATCHED'
    | 'DEFAULT_USED'
    | 'AUTO_SINGLE'
    | 'AMBIGUOUS'
    | 'NO_WAREHOUSES';

interface BuyerWarehouseOption {
    id: number;
    title: string;
    code: string;
    isDefault: boolean;
}

interface PreviewLine {
    lineId: string;
    shiplocCode: string;
    resolvedBuyerWarehouseId: number | null;
    resolvedBuyerWarehouseTitle: string | null;
    buyerWarehouseResolution: BuyerWarehouseResolution;
    availableBuyerWarehouses: BuyerWarehouseOption[];
    masterCode: string;
    productTitle: string | null;
    productFound: boolean;
    quantity: number;
    unitPrice: number;
    lineErrors: string[];
}

interface PreviewBuyer {
    arpCode: string;
    buyerTitle: string;
    buyerFound: boolean;
    lines: PreviewLine[];
}

interface PreviewData {
    previewToken: string;
    expiresAt: string;
    buyers: PreviewBuyer[];
    missingBuyerCodes?: string[];
    missingProductCodes?: string[];
    missingBuyerWarehouseCodes?: string[];
}

interface Props {
    show: boolean;
    onHide: () => void;
    onImported?: (orderIds: number[]) => void;
}

interface State {
    step: 'upload' | 'preview' | 'success';
    file: File | null;
    isLoading: boolean;
    preview: PreviewData | null;
    selectedLineIds: Set<string>;
    warehouseOverrides: { [lineId: string]: number };
    successIds: number[] | null;
    error: string | null;
}

const initialState: State = {
    step: 'upload',
    file: null,
    isLoading: false,
    preview: null,
    selectedLineIds: new Set(),
    warehouseOverrides: {},
    successIds: null,
    error: null,
};

class LogoXmlImportModal extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = initialState;
    }

    resetAndClose = () => {
        this.setState(initialState);
        this.props.onHide();
    };

    onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        if (file && !file.name.toLowerCase().endsWith('.xml')) {
            this.setState({ error: 'Sadece .xml dosyaları kabul edilir.', file: null });
            return;
        }
        this.setState({ file, error: null });
    };

    onPreview = async () => {
        const { file } = this.state;
        if (!file) return;

        this.setState({ isLoading: true, error: null });
        const response = await OrderApi.previewLogoXml(file);
        if (!response.success) {
            this.setState({ isLoading: false, error: response.error || 'Önizleme oluşturulamadı.' });
            return;
        }

        const preview: PreviewData = response.data;
        const buyers = preview.buyers || [];
        buyers.forEach(b => {
            (b.lines || []).forEach(l => {
                if (!l.lineErrors) l.lineErrors = [];
                if (!l.availableBuyerWarehouses) l.availableBuyerWarehouses = [];
            });
        });
        preview.buyers = buyers;

        const initialSelected = new Set<string>();
        buyers.forEach(b => {
            if (!b.buyerFound) return;
            b.lines.forEach(l => {
                if (this.isLineAutoSelectable(l, b)) initialSelected.add(l.lineId);
            });
        });

        this.setState({
            isLoading: false,
            preview,
            selectedLineIds: initialSelected,
            warehouseOverrides: {},
            step: 'preview',
        });
    };

    getEffectiveWarehouseId = (line: PreviewLine): number | null => {
        const override = this.state.warehouseOverrides[line.lineId];
        if (override !== undefined) return override;
        return line.resolvedBuyerWarehouseId;
    };

    isLineAutoSelectable = (line: PreviewLine, buyer: PreviewBuyer): boolean => {
        if (!buyer.buyerFound) return false;
        if ((line.lineErrors || []).length > 0) return false;
        if (!line.productFound) return false;
        const r = line.buyerWarehouseResolution;
        if (r === 'NO_WAREHOUSES' || r === 'AMBIGUOUS') return false;
        return true;
    };

    isLineSelectable = (line: PreviewLine, buyer: PreviewBuyer): boolean => {
        if (!buyer.buyerFound) return false;
        if ((line.lineErrors || []).length > 0) return false;
        if (!line.productFound) return false;
        if (line.buyerWarehouseResolution === 'NO_WAREHOUSES') return false;
        if (line.buyerWarehouseResolution === 'AMBIGUOUS') {
            return this.state.warehouseOverrides[line.lineId] !== undefined;
        }
        return true;
    };

    setWarehouseOverride = (line: PreviewLine, warehouseId: number | null) => {
        this.setState(prev => {
            const overrides = { ...prev.warehouseOverrides };
            const selected = new Set(prev.selectedLineIds);
            if (warehouseId === null) {
                delete overrides[line.lineId];
                if (line.buyerWarehouseResolution === 'AMBIGUOUS') {
                    selected.delete(line.lineId);
                }
            } else {
                overrides[line.lineId] = warehouseId;
                if (line.buyerWarehouseResolution === 'AMBIGUOUS') {
                    selected.add(line.lineId);
                }
            }
            return { warehouseOverrides: overrides, selectedLineIds: selected };
        });
    };

    selectAll = () => {
        const { preview } = this.state;
        if (!preview) return;
        this.setState(prev => {
            const next = new Set(prev.selectedLineIds);
            preview.buyers.forEach(b => {
                b.lines.forEach(l => {
                    if (this.isLineAutoSelectable(l, b)) next.add(l.lineId);
                    else if (l.buyerWarehouseResolution === 'AMBIGUOUS' && prev.warehouseOverrides[l.lineId] !== undefined) next.add(l.lineId);
                });
            });
            return { selectedLineIds: next };
        });
    };

    selectNone = () => {
        this.setState({ selectedLineIds: new Set() });
    };

    toggleLine = (lineId: string) => {
        this.setState(prev => {
            const next = new Set(prev.selectedLineIds);
            if (next.has(lineId)) next.delete(lineId);
            else next.add(lineId);
            return { selectedLineIds: next };
        });
    };

    toggleBuyer = (buyer: PreviewBuyer) => {
        this.setState(prev => {
            const next = new Set(prev.selectedLineIds);
            const selectable = buyer.lines.filter(l => this.isLineSelectable(l, buyer));
            const allSelected = selectable.length > 0 && selectable.every(l => next.has(l.lineId));
            if (allSelected) {
                selectable.forEach(l => next.delete(l.lineId));
            } else {
                selectable.forEach(l => next.add(l.lineId));
            }
            return { selectedLineIds: next };
        });
    };

    onConfirm = async () => {
        const { preview, selectedLineIds, warehouseOverrides } = this.state;
        if (!preview || selectedLineIds.size === 0) return;

        const overridesArray = Object.entries(warehouseOverrides)
            .filter(([lineId]) => selectedLineIds.has(lineId))
            .map(([lineId, buyerWarehouseId]) => ({ lineId, buyerWarehouseId }));

        this.setState({ isLoading: true, error: null });
        const response = await OrderApi.confirmLogoXml(
            preview.previewToken,
            Array.from(selectedLineIds),
            overridesArray,
        );
        if (!response.success) {
            this.setState({ isLoading: false, error: response.error || 'İçeri aktarım başarısız.' });
            return;
        }

        const ids: number[] = response.data || [];
        this.setState({ isLoading: false, successIds: ids, step: 'success' });
        if (this.props.onImported) this.props.onImported(ids);
    };

    backToUpload = () => {
        this.setState({ step: 'upload', file: null, preview: null, selectedLineIds: new Set(), warehouseOverrides: {}, error: null });
    };

    renderUploadStep() {
        const { file, isLoading, error } = this.state;
        return (
            <>
                <Form.Group>
                    <Form.Label className="fw-semibold">XML Dosyası Seç</Form.Label>
                    <Form.Control
                        type="file"
                        accept=".xml,text/xml,application/xml"
                        onChange={this.onFileChange}
                        disabled={isLoading}
                    />
                    <Form.Text className="text-muted">Sadece .xml dosyaları kabul edilir.</Form.Text>
                </Form.Group>
                {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
                <div className="d-flex justify-content-end mt-4 gap-2">
                    <Button variant="secondary" onClick={this.resetAndClose} disabled={isLoading}>İptal</Button>
                    <Button variant="primary" onClick={this.onPreview} disabled={!file || isLoading}>
                        {isLoading
                            ? <><Spinner animation="border" size="sm" className="me-2" />Önizleniyor...</>
                            : 'Önizle'}
                    </Button>
                </div>
            </>
        );
    }

    copyToClipboard = (values: string[]) => {
        const text = values.join(', ');
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).catch(() => { });
        }
    };

    renderMissingSection(icon: string, title: string, items: string[], eventKey: string) {
        if (!items || items.length === 0) return null;
        return (
            <Accordion.Item eventKey={eventKey}>
                <Accordion.Header>
                    <div className="d-flex align-items-center justify-content-between w-100 me-3">
                        <span>
                            <span className="me-2">{icon}</span>
                            <strong>{title}</strong>
                            <Badge bg="secondary" className="ms-2">{items.length}</Badge>
                        </span>
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={(e) => { e.stopPropagation(); this.copyToClipboard(items); }}
                        >
                            Kopyala 📋
                        </Button>
                    </div>
                </Accordion.Header>
                <Accordion.Body className="py-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
                    <ul className="mb-0 ps-3" style={{ fontFamily: 'monospace', fontSize: 13 }}>
                        {items.map((code, i) => <li key={i}>{code}</li>)}
                    </ul>
                </Accordion.Body>
            </Accordion.Item>
        );
    }

    renderMissingCard() {
        const { preview } = this.state;
        if (!preview) return null;
        const missingBuyers = preview.missingBuyerCodes || [];
        const missingProducts = preview.missingProductCodes || [];
        const missingWarehouses = preview.missingBuyerWarehouseCodes || [];
        if (missingBuyers.length === 0 && missingProducts.length === 0 && missingWarehouses.length === 0) {
            return null;
        }
        const defaultOpen: string[] = [];
        if (missingBuyers.length > 0) defaultOpen.push('mb');
        if (missingProducts.length > 0) defaultOpen.push('mp');
        if (missingWarehouses.length > 0) defaultOpen.push('mw');

        return (
            <Card className="mb-3 border-warning">
                <Card.Header className="bg-warning bg-opacity-25 fw-semibold">
                    ⚠️ Eksik Kayıtlar
                </Card.Header>
                <Card.Body className="p-0">
                    <Accordion alwaysOpen defaultActiveKey={defaultOpen} flush>
                        {this.renderMissingSection('🏢', 'Eksik Cariler', missingBuyers, 'mb')}
                        {this.renderMissingSection('📦', 'Eksik Ürünler', missingProducts, 'mp')}
                        {this.renderMissingSection('📍', 'Eksik Müşteri Depo Kodları', missingWarehouses, 'mw')}
                    </Accordion>
                </Card.Body>
            </Card>
        );
    }

    renderPreviewStep() {
        const { preview, selectedLineIds, isLoading, error } = this.state;
        if (!preview) return null;

        const allLines = preview.buyers.flatMap(b => b.lines);
        const totalLines = allLines.length;
        const erroredLines = allLines.filter(l => (l.lineErrors || []).length > 0).length;
        const ambiguousPending = allLines.filter(l =>
            l.buyerWarehouseResolution === 'AMBIGUOUS' &&
            this.state.warehouseOverrides[l.lineId] === undefined
        ).length;
        const selectedCount = selectedLineIds.size;

        if (preview.buyers.length === 0 || totalLines === 0) {
            return (
                <>
                    <Alert variant="warning">Bu XML'de işlenecek sipariş bulunamadı.</Alert>
                    <div className="d-flex justify-content-end mt-3 gap-2">
                        <Button variant="secondary" onClick={this.backToUpload}>Geri</Button>
                        <Button variant="outline-secondary" onClick={this.resetAndClose}>Kapat</Button>
                    </div>
                </>
            );
        }

        return (
            <>
                <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <Badge bg="info" className="me-2">{preview.buyers.length} cari</Badge>
                        <Badge bg="secondary" className="me-2">{totalLines} satır</Badge>
                        {erroredLines > 0 && <Badge bg="danger" className="me-2">{erroredLines} hatalı</Badge>}
                        {ambiguousPending > 0 && <Badge bg="warning" text="dark">{ambiguousPending} müşteri deposu seçilmedi</Badge>}
                    </div>
                    <small className="text-muted">
                        Önizleme bitiş: {new Date(preview.expiresAt).toLocaleTimeString('tr-TR')}
                    </small>
                </div>

                {this.renderMissingCard()}

                <div className="d-flex gap-2 mb-2 flex-wrap">
                    <Button size="sm" variant="outline-primary" onClick={this.selectAll}>
                        Tümünü Seç (uygunlar)
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={this.selectNone}>
                        Hiçbirini Seçme
                    </Button>
                </div>

                <Accordion alwaysOpen>
                    {preview.buyers.map((buyer, idx) => this.renderBuyer(buyer, idx))}
                </Accordion>

                {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}

                <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
                    <strong>{selectedCount} satır seçildi</strong>
                    <div className="d-flex gap-2">
                        <Button variant="secondary" onClick={this.backToUpload} disabled={isLoading}>Geri</Button>
                        <Button
                            variant="success"
                            onClick={this.onConfirm}
                            disabled={selectedCount === 0 || isLoading}
                        >
                            {isLoading
                                ? <><Spinner animation="border" size="sm" className="me-2" />İçeri Aktarılıyor...</>
                                : 'İçeri Aktar'}
                        </Button>
                    </div>
                </div>
            </>
        );
    }

    renderBuyer(buyer: PreviewBuyer, idx: number) {
        const { selectedLineIds } = this.state;
        const selectableLines = buyer.lines.filter(l => this.isLineSelectable(l, buyer));
        const selectedInBuyer = selectableLines.filter(l => selectedLineIds.has(l.lineId)).length;
        const allSelected = selectableLines.length > 0 && selectedInBuyer === selectableLines.length;
        const someSelected = selectedInBuyer > 0 && !allSelected;

        return (
            <Accordion.Item eventKey={`b-${idx}`} key={buyer.arpCode + '-' + idx}>
                <Accordion.Header>
                    <div className="d-flex align-items-center justify-content-between w-100 me-3 flex-wrap gap-2">
                        <div>
                            <strong>{buyer.buyerTitle || '(İsim yok)'}</strong>
                            <span className="text-muted ms-2" style={{ fontSize: 13 }}>({buyer.arpCode})</span>
                            <span className="ms-2 text-muted" style={{ fontSize: 13 }}>— {buyer.lines.length} satır</span>
                        </div>
                        <div>
                            {!buyer.buyerFound && <Badge bg="danger" className="me-2">Cari Bulunamadı</Badge>}
                            {buyer.buyerFound && selectableLines.length > 0 && (
                                <Badge bg={allSelected ? 'success' : someSelected ? 'warning' : 'secondary'}>
                                    {selectedInBuyer}/{selectableLines.length} seçili
                                </Badge>
                            )}
                        </div>
                    </div>
                </Accordion.Header>
                <Accordion.Body className="p-0">
                    {buyer.buyerFound && selectableLines.length > 0 && (
                        <div className="px-3 py-2 border-bottom" onClick={e => e.stopPropagation()}>
                            <Form.Check
                                id={`buyer-toggle-${idx}`}
                                type="checkbox"
                                label={allSelected ? 'Bu cariyi seçme' : 'Bu cariyi tümüyle seç'}
                                checked={allSelected}
                                onChange={() => this.toggleBuyer(buyer)}
                            />
                        </div>
                    )}
                    <Table size="sm" className="mb-0" hover>
                        <thead>
                            <tr>
                                <th style={{ width: 40 }}>Seç</th>
                                <th style={{ minWidth: 240 }}>Müşteri Deposu</th>
                                <th>Ürün</th>
                                <th style={{ width: 90 }}>Miktar</th>
                                <th style={{ width: 110 }}>Birim Fiyat</th>
                                <th style={{ width: 80 }}>Durum</th>
                            </tr>
                        </thead>
                        <tbody>
                            {buyer.lines.map(line => this.renderLine(line, buyer))}
                        </tbody>
                    </Table>
                </Accordion.Body>
            </Accordion.Item>
        );
    }

    renderWarehouseCell(line: PreviewLine) {
        const r = line.buyerWarehouseResolution;
        const overrideId = this.state.warehouseOverrides[line.lineId];
        const opts = line.availableBuyerWarehouses || [];

        if (r === 'NO_WAREHOUSES') {
            return <span className="text-danger">Cari'nin müşteri deposu yok</span>;
        }

        if (r === 'AMBIGUOUS') {
            const needsSelection = overrideId === undefined;
            return (
                <div>
                    <Form.Select
                        size="sm"
                        value={overrideId ?? ''}
                        isInvalid={needsSelection}
                        onChange={e => {
                            const v = e.target.value;
                            this.setWarehouseOverride(line, v ? parseInt(v, 10) : null);
                        }}
                    >
                        <option value="">— Müşteri deposu seçin —</option>
                        {opts.map(o => (
                            <option key={o.id} value={o.id}>
                                {o.title}{o.code ? ` (${o.code})` : ''}{o.isDefault ? ' ★' : ''}
                            </option>
                        ))}
                    </Form.Select>
                    {needsSelection && (
                        <small className="text-warning d-block mt-1">Seçim gerekli</small>
                    )}
                </div>
            );
        }

        const effectiveId = overrideId ?? line.resolvedBuyerWarehouseId;
        const effectiveTitle = overrideId !== undefined
            ? (opts.find(o => o.id === overrideId)?.title ?? line.resolvedBuyerWarehouseTitle)
            : line.resolvedBuyerWarehouseTitle;

        const badge = r === 'MATCHED'
            ? <Badge bg="success" className="ms-1">Eşleşti</Badge>
            : r === 'DEFAULT_USED'
                ? <Badge bg="warning" text="dark" className="ms-1">Varsayılan</Badge>
                : r === 'AUTO_SINGLE'
                    ? <Badge bg="warning" text="dark" className="ms-1">Tek depo</Badge>
                    : null;

        const allowDropdown = (r === 'AUTO_SINGLE' || r === 'DEFAULT_USED') && opts.length > 1;

        return (
            <div>
                <div>
                    <span>{effectiveTitle || '-'}</span>
                    {badge}
                </div>
                {allowDropdown && (
                    <Form.Select
                        size="sm"
                        className="mt-1"
                        value={effectiveId ?? ''}
                        onChange={e => {
                            const v = parseInt(e.target.value, 10);
                            this.setWarehouseOverride(line, v === line.resolvedBuyerWarehouseId ? null : v);
                        }}
                    >
                        {opts.map(o => (
                            <option key={o.id} value={o.id}>
                                {o.title}{o.code ? ` (${o.code})` : ''}{o.isDefault ? ' ★' : ''}
                            </option>
                        ))}
                    </Form.Select>
                )}
            </div>
        );
    }

    renderLine(line: PreviewLine, buyer: PreviewBuyer) {
        const selectable = this.isLineSelectable(line, buyer);
        const checked = this.state.selectedLineIds.has(line.lineId);
        const errs = line.lineErrors || [];
        const r = line.buyerWarehouseResolution;
        const isAmbiguousPending = r === 'AMBIGUOUS' && this.state.warehouseOverrides[line.lineId] === undefined;
        const hasError = errs.length > 0 || !buyer.buyerFound || !line.productFound || r === 'NO_WAREHOUSES';

        const rowStyle: React.CSSProperties | undefined = hasError
            ? { backgroundColor: '#fff5f5' }
            : isAmbiguousPending
                ? { backgroundColor: '#fff8e6' }
                : undefined;

        return (
            <tr key={line.lineId} style={rowStyle}>
                <td onClick={e => e.stopPropagation()}>
                    <Form.Check
                        id={`line-${line.lineId}`}
                        type="checkbox"
                        checked={checked}
                        disabled={!selectable}
                        onChange={() => this.toggleLine(line.lineId)}
                    />
                </td>
                <td onClick={e => e.stopPropagation()}>
                    {this.renderWarehouseCell(line)}
                </td>
                <td style={!line.productFound ? { color: '#c00' } : undefined}>
                    {line.productFound
                        ? line.productTitle
                        : <span title="Ürün bulunamadı">{line.masterCode}</span>}
                </td>
                <td>{line.quantity}</td>
                <td>{line.unitPrice?.toFixed(2)}</td>
                <td>
                    {hasError ? (
                        <OverlayTrigger
                            placement="left"
                            overlay={
                                <Tooltip>
                                    {!buyer.buyerFound && <div>Cari bulunamadı</div>}
                                    {!line.productFound && <div>Ürün bulunamadı: {line.masterCode}</div>}
                                    {r === 'NO_WAREHOUSES' && <div>Cari'nin müşteri deposu yok</div>}
                                    {errs.map((err, i) => <div key={i}>{err}</div>)}
                                </Tooltip>
                            }
                        >
                            <Badge bg="danger" style={{ cursor: 'help' }}>Hata</Badge>
                        </OverlayTrigger>
                    ) : isAmbiguousPending ? (
                        <Badge bg="warning" text="dark">Seçim Bekliyor</Badge>
                    ) : (
                        <Badge bg="success">Tamam</Badge>
                    )}
                </td>
            </tr>
        );
    }

    renderSuccessStep() {
        const { successIds } = this.state;
        const ids = successIds || [];
        return (
            <>
                <Alert variant="success" className="mb-0">
                    <h5 className="mb-2">{ids.length} sipariş başarıyla oluşturuldu</h5>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                        Sipariş ID'leri: {ids.join(', ')}
                    </div>
                </Alert>
                <div className="d-flex justify-content-end mt-4">
                    <Button variant="primary" onClick={this.resetAndClose}>Kapat</Button>
                </div>
            </>
        );
    }

    render() {
        const { show } = this.props;
        const { step } = this.state;
        const titles = {
            upload: 'Logo XML — Dosya Yükle',
            preview: 'Logo XML — Önizleme ve Seçim',
            success: 'Logo XML — Aktarım Tamamlandı',
        };

        return (
            <Modal show={show} onHide={this.resetAndClose} size="xl" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{titles[step]}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {step === 'upload' && this.renderUploadStep()}
                    {step === 'preview' && this.renderPreviewStep()}
                    {step === 'success' && this.renderSuccessStep()}
                </Modal.Body>
            </Modal>
        );
    }
}

export default LogoXmlImportModal;
